import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, FileTextIcon, GlobeIcon, PackageIcon, ShieldCheckIcon } from 'lucide-react';
import { bonuses, checkoutUrl, offerIncluded, product } from '../data/landing';
import { isValidCheckoutUrl } from '../utils/checkout';
import { CtaButton } from './CtaButton';
import { SectionHeading } from './SectionHeading';
const delivery = [{
  icon: FileTextIcon,
  label: 'Digital PDF'
}, {
  icon: GlobeIcon,
  label: 'Available worldwide'
}, {
  icon: PackageIcon,
  label: 'Nothing shipped'
}];
export function OfferSection() {
  const [showNote, setShowNote] = useState(false);
  const valid = isValidCheckoutUrl();
  const handleCheckout = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (valid) return;
    event.preventDefault();
    setShowNote(true);
  };
  return <section id="offer-details" className="bg-teal-tint py-12 md:py-24" data-id="element-112" data-mp="2t7m96chn2xaj">
      <div className="mx-auto max-w-[1180px] px-5 md:px-8" data-id="element-113" data-mp="2y4w58azja77c">
        <SectionHeading eyebrow="Everything in one pack" title="Your next 12 meetings," highlight="ready to lead." data-id="element-114" data-mp="12o1r8qy1mg28" />

        <div className="mx-auto max-w-[600px] rounded-xl border border-teal/20 bg-white px-5 py-8 shadow-[0_24px_60px_rgba(20,33,61,0.1)] sm:px-10 sm:py-10" data-id="element-115" data-mp="xvqs3qb4myaq">
          <div className="text-center" data-id="element-116" data-mp="ow3p502r1kfk">
            <span className="inline-block rounded-md bg-navy px-3 py-1.5 text-[11px] font-bold uppercase tracking-[2px] text-amber" data-id="element-117" data-mp="oxevn7nkx6ad">
              {product.volume} + all 5 bonuses
            </span>
            <img src={product.mockup} alt="Youth Ministry Ready box set with the 5 bonus books" width={1024} height={1536} className="mx-auto mt-8 h-auto w-full max-w-[280px] rounded-xl shadow-[0_20px_40px_rgba(20,33,61,0.2)]" data-id="element-118" data-mp="1gol0g1e8vs5z" />
            <h3 className="mt-8 font-display text-[38px] font-extrabold uppercase leading-none text-navy" data-id="element-119" data-mp="1843ufo7im8d">{product.name}</h3>
            <p className="mt-2 text-muted" data-id="element-120" data-mp="3sewzl408dt5i">{product.series} · 12 complete meetings + 5 bonus resources</p>
          </div>

          <div className="mt-8 border-t border-navy/10 pt-7" data-id="element-121" data-mp="2ng8c4a89edr">
            <p className="text-center text-xs font-bold uppercase tracking-[2.5px] text-navy" data-id="element-122" data-mp="2mx05fadq4cx">What's included</p>
            <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-123" data-mp="3kve4w0hmkbh3">
              {offerIncluded.map(item => <OfferItem key={item} data-id="element-124" data-mp="3b2zxar3v8an0">{item}</OfferItem>)}
            </ul>
            <p className="mt-7 text-center text-xs font-bold uppercase tracking-[2.5px] text-navy" data-id="element-125" data-mp="3fu89tw0hsc4e">The 5 bonuses</p>
            <ul className="mt-5 space-y-3" data-id="element-126" data-mp="315u4neflsbko">
              {bonuses.map(bonus => <OfferItem key={bonus.shortTitle} data-id="element-127" data-mp="3645f9rdq2q3y">
                  {bonus.shortTitle} — {bonus.subtitle}
                </OfferItem>)}
            </ul>
          </div>

          <div className="mt-8 border-t border-navy/10 pt-8 text-center" data-id="element-128" data-mp="2s705bgaw173f">
            <div className="flex items-start justify-center font-display font-extrabold text-navy" data-id="element-129" data-mp="2x2xhdaf4brae">
              <span className="mt-4 text-[40px] leading-none" data-id="element-130" data-mp="2di72axmnfwxa">$</span>
              <span className="text-[104px] leading-[0.85]" data-id="element-131" data-mp="28jvsuo09nofl">{product.price}</span>
              <span className="ml-2 self-end font-sans text-sm font-semibold text-muted" data-id="element-132" data-mp="2mivhvwroy5ca">{product.currency}</span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs font-bold uppercase tracking-[2px] text-muted" data-id="element-133" data-mp="2ifgpe6mkfwhy">
              <span data-id="element-134" data-mp="2weggrm1gqpht">One-time payment</span>
              <span data-id="element-135" data-mp="2rgonoop30xc1">No subscription</span>
            </div>

            <CtaButton href={valid ? checkoutUrl : '#checkout-note'} onClick={handleCheckout} className="mt-7 w-full" data-id="element-136" data-mp="366melceec14x">
              {product.ctaLabel} now
            </CtaButton>

            <div id="checkout-note" role="status" aria-live="polite" data-id="element-137" data-mp="319m7cy5t8sc8">
              <AnimatePresence data-id="element-138" data-mp="3f82c4jk448rq">
                {showNote && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-5 rounded-md border border-amber bg-amber-cream p-3 text-sm text-navy" data-id="element-139" data-mp="3a8pse7ojfhtd">
                    Purchase access will be available when this store launches.
                  </motion.p>}
              </AnimatePresence>
            </div>

            <p className="mt-5 flex items-center justify-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-140" data-mp="pm90o4bv60hd">
              <ShieldCheckIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-141" data-mp="ul3rntjzivck" />
              10-day money-back guarantee · No questions asked
            </p>
          </div>

          <ul className="mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 border-t border-navy/10 pt-6 text-[13px] font-medium text-muted" data-id="element-142" data-mp="13lic6qp4k6f0">
            {delivery.map(({
            icon: Icon,
            label
          }) => <li key={label} className="flex items-center gap-1.5" data-id="element-143" data-mp="2ex9gexjpqfwg">
                <Icon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-144" data-mp="2p1zm9ei8m45y" />
                {label}
              </li>)}
          </ul>
        </div>
        <p className="mt-5 text-center text-[13px] text-muted" data-id="element-145" data-mp="2k6ampb67wn9n">Price shown in USD.</p>
      </div>
    </section>;
}
function OfferItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-2.5 text-[15px] font-medium text-navy" data-id="element-146" data-mp="2b9theokjbjlt">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-cream" data-id="element-147" data-mp="26dup4pw35hg4">
        <CheckIcon className="h-3 w-3 text-amber-dark" strokeWidth={3} aria-hidden="true" data-id="element-148" data-mp="21fjbe42o6msm" />
      </span>
      {children}
    </li>;
}